import { useEffect, useState, useCallback } from "react";
import { Link } from "@tanstack/react-router";
import { Loader2, Sparkles, ArrowRight, X, Search, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/audit";
import { markNationSelected } from "@/lib/activation";
import { notifyNationChanged } from "@/hooks/useSelectedNation";
import { setFanContext } from "@/lib/adaptiveFan/adaptiveFanProfile";
import { useDailyWowActive } from "@/lib/ui/fabOrchestrator";
import { hasFirstEngagement, readSuggestedNation } from "@/lib/predictions/guestPrediction";
import { PREVIEW_MODE } from "@/lib/previewMode";

/**
 * PREVIEW GUEST MODE — allow un-authenticated visitors to pick a nation
 * and enter the Preview experience without signing up. Selection is
 * persisted in localStorage so it survives navigation within the
 * session; authentication is only required for features that need to
 * write to the server (leaderboards, rewards, etc.).
 */
const GUEST_NATION_KEY = "vs:guest_nation";
function readGuestNation(): string | null {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(GUEST_NATION_KEY); } catch { return null; }
}
function writeGuestNation(code: string) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(GUEST_NATION_KEY, code); } catch { /* noop */ }
}

type Team = { code: string; name: string; flag: string };

/**
 * P1-TEAM-SELECTOR — FIFA-style ordering for the expanded team picker.
 * Top countries are surfaced as a "favorites" row above the full list.
 * No schema changes — favorites are matched against the existing
 * `worldcup_teams` rows by ISO-3 code.
 */
const FAVORITE_CODES = ["ARG", "BRA", "ESP", "FRA", "ENG", "GER", "MEX", "USA"];

/**
 * NationGate — F1.2/F1.3.
 *
 * Blocks the host route until the signed-in user has set
 * `profiles.selection_team` (single source of truth, same field Nation Wars,
 * Creator Studio and Quinielas read). Mounted on Nation Wars, Quinielas and
 * Creator Studio. No nation = no access. Anonymous users see a CTA to
 * register (we do not capture nation at registration anymore — Phase 2).
 */
const SESSION_DISMISS_KEY = "vs:nation_gate:session_dismissed";

function sessionAlreadyDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try { return sessionStorage.getItem(SESSION_DISMISS_KEY) === "1"; } catch { return false; }
}
function markSessionDismissed() {
  if (typeof window === "undefined") return;
  try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* noop */ }
}

export function NationGate({
  surface,
  mode = "blocking",
}: {
  surface: "nation_wars" | "quinielas" | "creator_studio" | "home_live";
  mode?: "blocking" | "invite";
}) {
  const { isAuthenticated, user, authReady } = useAuth();
  const guestAllowed = PREVIEW_MODE && authReady && !isAuthenticated;
  const [nation, setNation] = useState<string | null | undefined>(undefined); // undefined = loading
  const [teams, setTeams] = useState<Team[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => sessionAlreadyDismissed());
  const [expanded, setExpanded] = useState(false);
  const suggestedNationCode = readSuggestedNation();
  const suggestedTeam = suggestedNationCode
    ? teams.find((t) => t.code === suggestedNationCode)
    : null;
  const [query, setQuery] = useState("");
  // POST-NATIONGATE ACTIVATION — when the user picks here, hold the
  // modal open with a success panel + CTA toward the first prediction.
  const [justPicked, setJustPicked] = useState<{ code: string; name: string; flag: string } | null>(null);
  // P0-RETENTION-02 — never compete with the Daily WOW splash/card.
  const dailyWowActive = useDailyWowActive();

  const loadNation = useCallback(async () => {
    if (!user?.id) {
      // PREVIEW GUEST MODE — hydrate nation from localStorage for guests.
      setNation(guestAllowed ? readGuestNation() : null);
      return;
    }
    const { data } = await supabase
      .from("profiles")
      .select("selection_team")
      .eq("id", user.id)
      .maybeSingle();
    setNation((data?.selection_team as string | null) ?? null);
  }, [user?.id, guestAllowed]);

  useEffect(() => { void loadNation(); }, [loadNation]);

  useEffect(() => {
    let alive = true;
    void supabase
      .from("worldcup_teams")
      .select("code,name,flag,qualified,display_order")
      .eq("qualified", true)
      .order("display_order", { ascending: true })
      .then(({ data }) => {
        if (!alive) return;
        setTeams((data ?? []).map((t) => ({ code: t.code, name: t.name, flag: t.flag })));
      });
    return () => { alive = false; };
  }, []);

  // P0-AUTH-06 — wait for the global auth bootstrap before deciding
  // whether to show the gate. Otherwise Android Chrome briefly sees
  // `isAuthenticated === false` on cold start and flashes the "Inicia
  // sesión" CTA over a fully authenticated user.
  const blocked = authReady && (
    (!isAuthenticated && !guestAllowed) ||
    nation === null ||
    !!justPicked
  );

  useEffect(() => {
    if (blocked) void trackEvent("nation_gate_shown", { surface, authenticated: isAuthenticated });
  }, [blocked, surface, isAuthenticated]);
  useEffect(() => {
    if (blocked && suggestedTeam) {
      void trackEvent("nation_auto_suggested", { surface, nation: suggestedTeam.code });
    }
  }, [blocked, suggestedTeam, surface]);

  if (!authReady || nation === undefined) return null;
  if (!blocked) return null;
  // P0 retention: never show NationGate twice in the same session.
  if (dismissed && !justPicked) return null;
  // P0-RETENTION-02 — yield to Daily WOW for invite-mode prompts.
  if (mode === "invite" && dailyWowActive && !justPicked) return null;
  // Prediction-First P0 — invite-mode NationGate is deferred until the
  // user has completed their first engagement (guest micro-prediction).
  // We never block the path to the first prediction with a nation prompt.
  if (mode === "invite" && !justPicked && !hasFirstEngagement()) {
    void trackEvent("nationgate_deferred", { surface, reason: "no_first_engagement" });
    return null;
  }

  const inviteMode = mode === "invite" && !justPicked;

  const onPick = async (code: string) => {
    if (saving) return;
    setSaving(code);
    if (user?.id) {
      const { error } = await supabase.from("profiles").update({ selection_team: code }).eq("id", user.id);
      setSaving(null);
      if (error) return;
    } else if (guestAllowed) {
      // PREVIEW GUEST MODE — persist locally; no server round-trip.
      writeGuestNation(code);
      setSaving(null);
    } else {
      setSaving(null);
      return;
    }
    void trackEvent("nation_selected", { surface, nation: code });
    markNationSelected();
    // Sync AdaptiveFanProfile so HypeMeter / botón de apoyo reflejen la
    // nación elegida inmediatamente (evita mostrar España cuando el usuario
    // eligió Argentina).
    try { setFanContext({ favoriteTeam: code }); } catch { /* ignore */ }
    notifyNationChanged();
    setNation(code);
    const picked = teams.find((t) => t.code === code);
    setJustPicked({ code, name: picked?.name ?? code, flag: picked?.flag ?? "🏳️" });
  };

  return (
    <div
      className={
        inviteMode
          ? "sticky top-[64px] z-30 mx-auto w-full max-w-3xl px-3"
          : "fixed inset-0 z-[90] flex items-center justify-center bg-background/95 px-4 backdrop-blur-md"
      }
    >
      <div
        className={
          inviteMode
            ? "relative rounded-2xl border border-primary/40 bg-background/95 p-4 shadow-lg backdrop-blur-md"
            : "w-full max-w-lg rounded-3xl border border-white/10 bg-gradient-to-b from-[oklch(0.18_0.02_270)]/95 to-[oklch(0.08_0.02_270)]/95 p-6 shadow-2xl"
        }
      >
        {inviteMode && (
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => { markSessionDismissed(); setDismissed(true); }}
            className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-accent/40 text-muted-foreground hover:bg-accent/70 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        {justPicked ? (
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-3xl shadow-lg">
              {justPicked.flag}
            </div>
            <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-400">
              <Sparkles className="mr-1 inline h-3 w-3" />
              {justPicked.name} activada
            </p>
            <h2 className="mt-1 text-2xl font-black text-foreground">
              Tu nación ya está lista para competir.
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Cada predicción correcta sumará puntos para {justPicked.name}. Empieza ahora.
            </p>
            <Link
              to="/quinielas"
              onClick={() => { void trackEvent("activation_cta_clicked", { step: "prediction", surface }); }}
              className="mt-5 flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-black text-primary-foreground shadow-lg"
            >
              Haz tu primera predicción
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={() => setJustPicked(null)}
              className="mt-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground"
            >
              Más tarde
            </button>
          </div>
        ) : (
        <>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">Mundial 2026</p>
        <h2 className={`mt-1 font-black text-foreground ${inviteMode ? "text-lg" : "text-2xl"}`}>
          {inviteMode
            ? "Únete a tu nación"
            : (isAuthenticated || guestAllowed) ? "Elige tu nación para continuar" : "Inicia sesión para continuar"}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {(isAuthenticated || guestAllowed)
            ? "Tu nación define tu equipo en Nation Wars, tus puntos y tus recompensas. Solo puedes elegir una."
            : "Necesitas una cuenta para participar y que tus puntos cuenten para tu nación."}
        </p>

        {(!isAuthenticated && !guestAllowed) ? (
          <div className="mt-5 flex flex-col gap-2">
            <Link to="/registro" className="rounded-xl bg-primary px-5 py-3 text-center text-sm font-bold text-primary-foreground shadow">
              Crear cuenta
            </Link>
            <Link to="/login" search={{ from: undefined }} className="rounded-xl border border-border bg-background px-5 py-3 text-center text-sm font-bold text-foreground">
              Ya tengo cuenta
            </Link>
          </div>
        ) : inviteMode && !expanded ? (
          <div className="mt-3 flex flex-col gap-2">
            {suggestedTeam && (
              <div className="rounded-xl border border-primary/30 bg-primary/10 p-3 text-xs">
                <p className="font-bold text-foreground">
                  Detectamos que apoyas a {suggestedTeam.flag} {suggestedTeam.name}
                </p>
                <button
                  type="button"
                  disabled={!!saving}
                  onClick={() => {
                    void trackEvent("nation_auto_confirmed", { surface, nation: suggestedTeam.code });
                    void onPick(suggestedTeam.code);
                  }}
                  className="mt-2 w-full rounded-lg bg-primary px-4 py-2 text-xs font-black text-primary-foreground shadow active:scale-95 disabled:opacity-60"
                >
                  Confirmar {suggestedTeam.flag} {suggestedTeam.name}
                </button>
              </div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="flex-1 rounded-xl bg-primary px-5 py-2.5 text-sm font-black text-primary-foreground shadow"
              >
                {suggestedTeam ? "Cambiar nación" : "Elegir mi nación"}
              </button>
              <button
                type="button"
                onClick={() => { markSessionDismissed(); setDismissed(true); }}
                className="rounded-xl border border-border bg-background px-4 py-2.5 text-xs font-bold text-muted-foreground"
              >
                Más tarde
              </button>
            </div>
          </div>
        ) : (
          (() => {
            const q = query.trim().toLowerCase();
            const filtered = q
              ? teams.filter((t) =>
                  t.name.toLowerCase().includes(q) ||
                  t.code.toLowerCase().includes(q),
                )
              : teams;
            const favoriteTeams = !q
              ? FAVORITE_CODES.map((c) => teams.find((t) => t.code === c)).filter(
                  (x): x is Team => !!x,
                )
              : [];
            const restTeams = !q
              ? teams.filter((t) => !FAVORITE_CODES.includes(t.code))
              : filtered;
            const renderTeam = (t: Team) => (
              <button
                key={t.code}
                type="button"
                disabled={!!saving}
                onClick={() => void onPick(t.code)}
                className="flex items-center gap-2 rounded-xl border border-border/60 bg-background/60 px-3 py-2 text-left text-sm font-bold transition hover:border-primary/60 hover:bg-accent/30 disabled:opacity-50"
              >
                <span className="text-lg">{t.flag}</span>
                <span className="truncate">{t.name}</span>
                {saving === t.code && <Loader2 className="ml-auto h-3 w-3 animate-spin" />}
              </button>
            );
            return (
              <div className="mt-5 max-h-[60vh] space-y-3 overflow-y-auto">
                {/* Search */}
                <div className="sticky top-0 z-10 -mt-1 bg-background/95 pb-1 pt-0.5 backdrop-blur">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      inputMode="search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Buscar nación o país…"
                      aria-label="Buscar nación"
                      className="w-full rounded-lg border border-border/70 bg-background/80 py-2 pl-9 pr-3 text-sm font-bold text-foreground placeholder:text-muted-foreground focus:border-primary/70 focus:outline-none"
                    />
                  </div>
                </div>

                {teams.length === 0 && (
                  <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando naciones…
                  </div>
                )}

                {/* Favorites */}
                {favoriteTeams.length > 0 && (
                  <div>
                    <p className="mb-1.5 flex items-center gap-1 px-0.5 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                      <Star className="h-3 w-3 text-primary" /> Favoritos
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {favoriteTeams.map(renderTeam)}
                    </div>
                  </div>
                )}

                {/* Full list / filtered results */}
                <div>
                  {!q && favoriteTeams.length > 0 && (
                    <p className="mb-1.5 px-0.5 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                      Todas las naciones FIFA
                    </p>
                  )}
                  {q && filtered.length === 0 && teams.length > 0 && (
                    <p className="px-0.5 py-2 text-xs text-muted-foreground">
                      Sin resultados para “{query}”.
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {restTeams.map(renderTeam)}
                  </div>
                </div>
              </div>
            );
          })()
        )}
        </>
        )}
      </div>
    </div>
  );
}